import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAudioTranscripcion } from '../../services/clientService';
import { getSacTranscriptById } from '../../services/marketingReportService';
import CallChat2 from '../../components/Chat2/CallChat2';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { marketingI18n } from './i18n';
import '../Clients/ClientsDetail/partial/callDetail.css';

const hasValue = (value) =>
  value !== null && value !== undefined && String(value).trim() !== '';

const normalizeSpeaker = (value) => {
  const speaker = String(value || '')
    .trim()
    .toLowerCase();
  if (speaker === 'agente' || speaker === 'agent') return 'Agente';
  if (speaker === 'cliente' || speaker === 'customer') return 'Cliente';
  return 'Agente';
};

const buildAudioTextFromTranscription = (transcription) => {
  const text = String(transcription || '').trim();
  if (!text) return '';

  // Keep legacy format untouched: start-end-speaker-text;...
  if (/^\d+-\d+-[^-]+-/.test(text) || /;\s*\d+-\d+-[^-]+-/.test(text)) {
    return text;
  }

  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  let seconds = 0;

  return lines
    .map((line) => {
      const match = line.match(/^(Agente|Cliente|Agent|Customer)\s*:\s*(.+)$/i);
      const speaker = normalizeSpeaker(match ? match[1] : 'Agente');
      const message = (match ? match[2] : line).replace(/;/g, ',').trim();

      if (!message) return null;

      const start = seconds;
      const duration = Math.max(2, Math.ceil(message.length / 14));
      const end = start + duration;
      seconds = end;

      return `${start}-${end}-${speaker}-${message}`;
    })
    .filter(Boolean)
    .join(';');
};

const getFeelingConfig = (value, t) => {
  const normalized = String(value || '')
    .toLowerCase()
    .trim();

  if (normalized === 'positive' || normalized === 'positivo') {
    return { text: t('mk_cd_feelingPositive'), color: 'success' };
  }
  if (normalized === 'negative' || normalized === 'negativo') {
    return { text: t('mk_cd_feelingNegative'), color: 'danger' };
  }
  if (normalized === 'neutral' || normalized === 'neutro') {
    return { text: t('mk_cd_feelingNeutral'), color: 'info' };
  }

  return { text: value, color: 'light' };
};

const formatDate = (dateString) => {
  if (!dateString) return '--';

  return new Date(dateString).toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const MarketingCallDetail = ({ DesignSystemComponents }) => {
  const { Button, Card, Input, Badge } = DesignSystemComponents;
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useScopedTranslation(marketingI18n);

  const [callData, setCallData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioError, setAudioError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const abortController = new AbortController();

    const fetchCallDetail = async () => {
      if (!id) {
        setError(t('mk_cd_invalidId'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const data = await getSacTranscriptById(id, abortController.signal);
        setCallData(data);
      } catch (err) {
        if (err.name !== 'AbortError') {
          const status = err?.response?.status;

          if (status === 404) {
            setError(t('mk_cd_notFound'));
          } else if (status === 400) {
            setError(t('mk_cd_invalidId'));
          } else {
            setError(err.message || t('mk_cd_loadError'));
          }

          console.error('Error fetching marketing call detail:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCallDetail();

    return () => abortController.abort();
  }, [id]);

  const chatCallData = useMemo(() => {
    if (!callData) return null;

    return {
      ...callData,
      audioText: hasValue(callData.audioText)
        ? callData.audioText
        : buildAudioTextFromTranscription(callData.transcription),
    };
  }, [callData]);

  const handleLoadAudio = async () => {
    setAudioError('');

    try {
      const blob = await getAudioTranscripcion(id);
      const url = window.URL.createObjectURL(blob);
      setAudioUrl(url);
    } catch (err) {
      console.error('Error loading audio:', err);
      setAudioError(t('mk_cd_audioError'));
    }
  };

  if (loading) {
    return (
      <div className='container-client-detail'>
        <p className='caption'>{t('mk_cd_loading')}</p>
      </div>
    );
  }

  if (error || !callData) {
    return (
      <div className='container-client-detail'>
        <Button
          variant='naked'
          iconStart='chevronLeft'
          label={t('mk_cd_back')}
          size='sm'
          onClick={() => navigate(-1)}
        />
        <p className='caption text-danger mt-3'>
          {error || t('mk_cd_notFound')}
        </p>
      </div>
    );
  }

  return (
    <div className='container client-detail'>
      <div className='d-flex align-items-center gap-2 mb-2'>
        <Button
          variant='naked'
          iconStart='chevronLeft'
          label={t('mk_cd_back')}
          size='sm'
          onClick={() => navigate(-1)}
        />
      </div>

      <div className='row'>
        <div className='calldetail-container'>
          <div className='col-6'>
            <Card specialPadding='p-4'>
              <div className='mb-4'>
                <h2 className='fw-medium mb-1'>{t('mk_cd_callTitle')}</h2>
                <p className='caption font-light mb-0'>
                  {t('mk_cd_id')} {callData.id}
                </p>
              </div>

              <div>
                {[
                  {
                    label: t('mk_cd_audioDate'),
                    value: hasValue(callData.audioDate)
                      ? formatDate(callData.audioDate)
                      : null,
                  },
                  {
                    label: t('mk_cd_createdAt'),
                    value: hasValue(callData.createdAt)
                      ? formatDate(callData.createdAt)
                      : null,
                  },
                  { label: 'Bucket', value: callData.bucket },
                  { label: 'S3 Path', value: callData.s3Path },
                  {
                    label: t('mk_cd_feeling'),
                    render: hasValue(callData.feeling) ? (
                      <Badge
                        text={getFeelingConfig(callData.feeling, t).text}
                        color={getFeelingConfig(callData.feeling, t).color}
                        size='sm'
                      />
                    ) : null,
                  },
                ]
                  .filter((item) => item.render || hasValue(item.value))
                  .map((item) => (
                    <div
                      key={item.label}
                      className='d-flex justify-content-between mb-2'
                    >
                      <p className='caption fw-semibold'>{item.label}</p>
                      {item.render || <p className='caption'>{item.value}</p>}
                    </div>
                  ))}
              </div>

              {hasValue(callData.summary) ? (
                <>
                  <hr className='line mt-4 mb-3' />
                  <h3 className='fw-bold mb-3'>{t('mk_cd_summary')}</h3>
                  <div className='content-break summary-break'>
                    <p className='body'>{callData.summary}</p>
                  </div>
                </>
              ) : null}
            </Card>
          </div>

          <div className='col-6'>
            <Card notPadding>
              <div className='px-4 pt-4'>
                <div className='d-flex justify-content-center mb-3'>
                  {audioUrl ? (
                    <audio controls className='w-100' src={audioUrl} />
                  ) : (
                    <Button
                      variant='primary'
                      label={t('mk_cd_downloadAudio')}
                      size='sm'
                      onClick={handleLoadAudio}
                    />
                  )}
                </div>

                {audioError ? (
                  <p className='caption text-danger mb-3 text-center'>
                    {audioError}
                  </p>
                ) : null}

                <Input
                  icon='search'
                  name='searchAudio'
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={t('mk_cd_searchPlaceholder')}
                  type='text'
                  value={searchTerm}
                />
              </div>

              <hr className='line my-0' />

              <div className='content-break chat-break'>
                <CallChat2 callData={chatCallData} searchQuery={searchTerm} />
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingCallDetail;
